import { Component, EventEmitter, Input, Output } from '@angular/core';
import { IConnectionMapping } from '../../models/integration/connection-mapping.model';
import { IIntegrationConnection } from '../../models/integration/integration-connection.model';
import { VendorDefinition } from '../../models/integration/vendor-definition.model';

/**
 * Vendor-agnostic info modal for a per-org integration (e.g. D365 BC) shown in a
 * per-company context (Company page, Annual Report import). The connection is set up
 * and managed in the Organization Settings, which only admins can reach.
 *
 *   - If the company IS mapped: shows the tenant / environment / linked company and a
 *     note that management happens in the Org Settings.
 *   - If NOT mapped: explains it must be connected in the Org Settings.
 *   - Admins get a "go to Org Settings" CTA; non-admins are asked to contact an admin.
 *
 * Dumb/presentational: the host resolves `isAdmin` and owns the navigation triggered
 * by `goToAdmin`, so this lib stays free of auth + app-routing dependencies.
 */
@Component({
    selector: 'aumico-integration-go-to-admin-modal',
    templateUrl: './integration-go-to-admin-modal.component.html',
    styleUrls: ['./integration-go-to-admin-modal.component.scss'],
})
export class IntegrationGoToAdminModalComponent {
    @Input() public vendor!: VendorDefinition;

    /** The company's mapping for this vendor, if any. Null when unmapped. */
    @Input() public mapping: IConnectionMapping | null = null;

    /**
     * The org connection the mapping points at, if resolved. Null when not mapped or
     * the connection was deleted (validate-on-read: mapping exists but connection gone).
     */
    @Input() public connection: IIntegrationConnection | null = null;

    /** Whether the current user is an org/app admin. Resolved by the host. */
    @Input() public isAdmin = false;

    /** Admin chose to open the Org Settings to set up / manage this integration. */
    @Output() public goToAdmin = new EventEmitter<void>();
    @Output() public closed = new EventEmitter<void>();

    public get isConnected(): boolean {
        return this.mapping != null;
    }

    /** True when the mapping points at a connection that no longer resolves / is unhealthy. */
    public get isBroken(): boolean {
        return this.mapping != null && this.connection == null;
    }

    public onPrimary(): void {
        if (this.isAdmin) {
            this.goToAdmin.emit();
        } else {
            this.closed.emit();
        }
    }

    public onClose(): void {
        this.closed.emit();
    }
}
